// GriddedReference.jsx
// Standalone flash-design reference: image + mirror + griddle grid overlay.
// Extracted for Practice Studio — the ArtistQueueView render stays inline
// (it's entangled with the queue's gesture/persistence machinery), but both
// draw the same GridOverlay.
//
// Mirroring defaults ON: pancake art is drawn in reverse, so practicing
// un-mirrored builds the wrong muscle memory. The mirror wrapper is separate
// from the sizing container (same separation ArtistQueueView uses so
// transforms never corrupt layout math).
//
// With no imageUrl (freeform practice) it renders the bare griddle circle.

import { useEffect, useRef, useState } from "react";
import GridOverlay from "../GridOverlay";
import { STAFF_THEME, FONT } from "../theme/staff";

const BRAND = STAFF_THEME;

export default function GriddedReference({ imageUrl, label }) {
  const containerRef = useRef(null);
  const [size, setSize] = useState(0);
  const [mirrored, setMirrored] = useState(true);
  const [showGrid, setShowGrid] = useState(true);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => setSize(el.clientWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div>
      <div
        ref={containerRef}
        style={{
          position: "relative",
          width: "100%",
          maxWidth: "560px",
          aspectRatio: "1 / 1",
          margin: "0 auto",
          background: "rgba(0,0,0,0.35)",
          borderRadius: "14px",
          border: `1px solid ${BRAND.border}`,
          overflow: "hidden",
        }}
      >
        {imageUrl ? (
          <div
            style={{
              position: "absolute",
              inset: 0,
              transform: mirrored ? "scaleX(-1)" : "none",
              transition: "transform 0.3s",
            }}
          >
            <img
              src={imageUrl}
              alt={label || "flash design"}
              style={{ width: "100%", height: "100%", objectFit: "contain" }}
              draggable={false}
            />
          </div>
        ) : (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: BRAND.dim,
              fontFamily: FONT.display,
              fontSize: "0.9rem",
            }}
          >
            Freeform — your canvas
          </div>
        )}
        {showGrid && size > 0 && <GridOverlay size={size} showLabels={false} />}
        {imageUrl && mirrored && (
          <div
            style={{
              position: "absolute",
              top: "0.5rem",
              left: "0.5rem",
              background: "rgba(0,0,0,0.55)",
              color: BRAND.gold,
              fontFamily: FONT.display,
              fontSize: "0.62rem",
              fontWeight: 700,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              padding: "0.2rem 0.5rem",
              borderRadius: "6px",
              pointerEvents: "none",
            }}
          >
            🪞 mirrored — draw it like you see it
          </div>
        )}
      </div>

      <div style={{ display: "flex", gap: "0.5rem", justifyContent: "center", marginTop: "0.6rem" }}>
        {imageUrl && (
          <ToggleChip
            active={mirrored}
            onClick={() => setMirrored((m) => !m)}
            label="🪞 Mirror"
          />
        )}
        <ToggleChip
          active={showGrid}
          onClick={() => setShowGrid((g) => !g)}
          label="⊞ Grid"
        />
      </div>
    </div>
  );
}

function ToggleChip({ active, onClick, label }) {
  return (
    <button
      onClick={onClick}
      style={{
        background: active ? BRAND.gold : "transparent",
        color: active ? BRAND.dark : BRAND.muted,
        border: active ? `1px solid ${BRAND.gold}` : `1px solid ${BRAND.border}`,
        borderRadius: "999px",
        padding: "0.3rem 0.8rem",
        fontSize: "0.78rem",
        fontWeight: 700,
        cursor: "pointer",
      }}
    >
      {label}
    </button>
  );
}
